import {
  FatInvestorsPageAllAdvisoryFirmListQuery,
  FatInvestorsPageListEntitiesQuery,
  FatInvestorsPageListEntitiesTotalQuery,
  FatInvestorsPageListFirmsQuery,
  FatInvestorsPageListFirmsTotalQuery,
  FatInvestorsPageListInvestorsQuery,
  FatInvestorsPageListInvestorsTotalQuery,
  InvestorSortBy,
  InvestorSortDirection,
  PaginateSortDirection,
  TenantSortBy
} from '@/__generated__/graphql';
import client from '@/apollo-client';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import { AddPageIcon } from '@/assets/static/icons/add-page-icon';
import { DollarSignCircleIcon } from '@/assets/static/icons/dollar-sign-circle-icon';
import { GearPageIcon } from '@/assets/static/icons/gear-page-icon';
import { HistoryIcon } from '@/assets/static/icons/history-icon';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { TenantType } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../basicComponents/button';
import { ISelectValue } from '../basicComponents/select';
import { createSimpleSelectOption } from '../basicComponents/select/utils';
import SpecialControlHeader from '../dealCatalog-page/switcher/specialControlHeader';
import Header from '../header';
import { LoaderOnWholeScreen } from '../loaders/loader-on-whole-screen';
import { initialValuesFromTable } from '../table/constants';
import FilterPage from '../table/filterPage';
import TablePagination from '../table/tablePagination';
import { SORT_ORDER } from '../table/types';
import { advisorTableSort, entityTableSort, initialListValue, investorTableSort } from './constants';
import AddNewInvestorEntity from './modals/addNewInvestorEntity';
import {
  ALL_ADVISORY_FIRM_LIST_QUERY,
  GET_INVESTOR_LEGAL_ENTITIES_IDS_QUERY,
  GET_TOTAL_LIST_ADVISORS_QUERY,
  GET_TOTAL_LIST_ENTITY_QUERY,
  GET_TOTAL_LIST_INVESTORS_QUERY,
  LIST_ADVISORS_QUERY,
  LIST_ENTITY_QUERY,
  LIST_INVESTORS_QUERY
} from './queries';
import Table from './table/table';
import { CurrentStatus } from './types';

interface IMobileSort {
  title: string;
  key: string;
  asc: boolean;
}

export const Investors = () => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const {
    searchParams,
    setSearchParams,
    onChangeSearch,
    onChangeToggle,
    saveScrollPostion,
    makeScroll,
    onChangePrimaryFilter,
    onChangeSort,
    onChangePaginationData
  } = useSearchParamsManager();

  const q = searchParams.get('q') || '';
  const viewBy = searchParams.get('viewBy');
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const key = searchParams.get('key') || 'TOTAL_COMMITMENTS';
  const asc = searchParams.get('asc') === 'true' || false;
  const position = searchParams.get('position') || 0;
  const primaryFilter = searchParams.get('primaryFilter') || 'All Advisory Firms';
  const toggle = searchParams.get('toggle') === null || searchParams.get('toggle') === 'true';

  const [primaryFilterList, setPrimaryFilterList] = useState<ISelectValue[]>([]);
  const [viewByList, setViewByList] = useState<ISelectValue[]>([]);
  const [listAdvisors, setListAdvisors] = useState<FatInvestorsPageListFirmsQuery['listFirms']>({ ...initialListValue });
  const [listInvestor, setListInvestor] = useState<FatInvestorsPageListInvestorsQuery['listInvestors']>({ ...initialListValue });
  const [listEntity, setListEntity] = useState<FatInvestorsPageListEntitiesQuery['listEntities']>({ ...initialListValue });
  const [firmsTotal, setFirmsTotal] = useState<number | null>(null);
  const [investorsTotal, setInvestorsTotal] = useState<number | null>(null);
  const [entitiesTotal, setEntitiesTotal] = useState<number | null>(null);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'add-new-investor-entity' });
  const [mobileSortItems, setMobileSortItems] = useState<IMobileSort[]>([]);
  const [mobileSort, setMobileSort] = useState('');
  const sortItem = mobileSortItems.find((item) => item.title === mobileSort);
  const mobileSortList = mobileSortItems.map((item) => item.title);

  useEffect(() => {
    if (!user) return;

    if (user.tenant.type === TenantType.ADVISORY_FIRM) {
      setViewByList([createSimpleSelectOption('Investor Household'), createSimpleSelectOption('Investor Entity')]);
      setPrimaryFilterList([createSimpleSelectOption('', 'All Investors')]);
      if (viewBy === 'Investor Household' || viewBy === 'Investor Entity') return;
      onChangeViewBy('Investor Household');
      return;
    }

    if (user.tenant.type === TenantType.PLATFORM_MANAGER) {
      setViewByList([
        createSimpleSelectOption('Advisory Firm'),
        createSimpleSelectOption('Investor Household'),
        createSimpleSelectOption('Investor Entity')
      ]);
      setPrimaryFilterList([createSimpleSelectOption('', 'All Advisory Firms')]);
      if (!viewBy) {
        onChangeViewBy('Advisory Firm');
      }
      return;
    }
  }, [user]);

  useEffect(() => {
    if (!viewBy && user) {
      onChangeViewBy(user.tenant.type === TenantType.PLATFORM_MANAGER ? 'Advisory Firm' : 'Investor Household');
      return;
    }
  }, [viewBy]);

  const filteredMobileSortItems = (items: IMobileSort[]) => {
    if (user && user.tenant.type === TenantType.ADVISORY_FIRM) {
      return items.filter((item) => item.key !== 'ADVISORY_FIRMS');
    }
    return items;
  };

  useEffect(() => {
    if (!isMobile) return;

    switch (viewBy) {
      case 'Advisory Firm':
        const filteredAdvisoryFirmTableSort = filteredMobileSortItems(advisorTableSort);
        setMobileSortItems(filteredAdvisoryFirmTableSort);
        setMobileSort(filteredAdvisoryFirmTableSort[0].title);
        break;
      case 'Investor Household':
        const filteredInvestorTableSort = filteredMobileSortItems(investorTableSort);
        setMobileSortItems(filteredInvestorTableSort);
        setMobileSort(filteredInvestorTableSort[0].title);
        break;
      case 'Investor Entity':
        const filteredEntityTableSort = filteredMobileSortItems(entityTableSort);
        setMobileSortItems(filteredEntityTableSort);
        setMobileSort(filteredEntityTableSort[0].title);
        break;
      default:
        break;
    }
  }, [viewBy, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    getCurrentStatus().refetch({
      data: {
        sortDirection: sortItem?.asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
        sortBy: sortItem?.key
      }
    });
  }, [mobileSort]);

  const { data: advisoryFirmListData } = useQuery<FatInvestorsPageAllAdvisoryFirmListQuery>(ALL_ADVISORY_FIRM_LIST_QUERY, {
    skip: Boolean(user && user.tenant.type === TenantType.ADVISORY_FIRM),
    variables: {
      data: {
        sortBy: TenantSortBy.Name,
        sortDirection: PaginateSortDirection.Asc
      }
    },
    onCompleted: ({ allAdvisoryFirmList }) => {
      setPrimaryFilterList([
        createSimpleSelectOption('', 'All Advisory Firms'),
        ...new Set(allAdvisoryFirmList.map((el) => createSimpleSelectOption(el.name)))
      ]);
    }
  });

  const { loading: getTotalListAdvisorsLoading } = useQuery<FatInvestorsPageListFirmsTotalQuery>(GET_TOTAL_LIST_ADVISORS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    variables: {
      data: {
        sortBy: InvestorSortBy.AdvisoryFirms,
        sortDirection: InvestorSortDirection.Asc,
        limit: 1,
        includeRecommendations: true
      }
    },
    onCompleted: ({ listFirms }: { listFirms: FatInvestorsPageListFirmsTotalQuery['listFirms'] }) => {
      setFirmsTotal(listFirms.total);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { loading: getTotalListInvestorsLoading } = useQuery<FatInvestorsPageListInvestorsTotalQuery>(GET_TOTAL_LIST_INVESTORS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    variables: {
      data: {
        sortBy: InvestorSortBy.Investor,
        sortDirection: SORT_ORDER.ASC,
        limit: 1,
        includeRecommendations: true
      }
    },
    onCompleted: ({ listInvestors }: { listInvestors: FatInvestorsPageListInvestorsTotalQuery['listInvestors'] }) => {
      setInvestorsTotal(listInvestors.total);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { loading: getTotalListEntityLoading } = useQuery<FatInvestorsPageListEntitiesTotalQuery>(GET_TOTAL_LIST_ENTITY_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    variables: {
      data: {
        sortBy: InvestorSortBy.Entity,
        sortDirection: SORT_ORDER.ASC,
        limit: 1,
        includeRecommendations: true
      }
    },
    onCompleted: ({ listEntities }: { listEntities: FatInvestorsPageListEntitiesTotalQuery['listEntities'] }) => {
      setEntitiesTotal(listEntities.total);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const {
    data: listAdvisorData,
    refetch: listAdvisorRefetch,
    loading: listAdvisorLoading
  } = useQuery<FatInvestorsPageListFirmsQuery>(LIST_ADVISORS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    skip: viewBy !== 'Advisory Firm',
    variables: {
      data: {
        sortBy: key,
        sortDirection: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
        search: q.toLowerCase().trim(),
        limit: Number(limit),
        offset: Number(offset),
        includeRecommendations: toggle,
        filter: ['All Advisory Firms'].includes(primaryFilter) ? null : primaryFilter
      }
    },
    onCompleted: ({ listFirms }: { listFirms: FatInvestorsPageListFirmsQuery['listFirms'] }) => {
      setListAdvisors(listFirms);
      makeScroll(position);
    },
    onError: (error) => {
      console.error(error);
      if (listAdvisorData) {
        setListAdvisors(listAdvisorData.listFirms || { ...initialListValue });
      }
    }
  });

  const {
    data: listInvestorsData,
    refetch: listInvestorsRefetch,
    loading: listInvestorsLoading
  } = useQuery<FatInvestorsPageListInvestorsQuery>(LIST_INVESTORS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    skip: viewBy !== 'Investor Household',
    variables: {
      data: {
        sortBy: key,
        sortDirection: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
        search: q.toLowerCase().trim(),
        limit: Number(limit),
        offset: Number(offset),
        includeRecommendations: toggle,
        filter: ['All Advisory Firms'].includes(primaryFilter) ? null : primaryFilter
      }
    },
    onCompleted: ({ listInvestors }: { listInvestors: FatInvestorsPageListInvestorsQuery['listInvestors'] }) => {
      setListInvestor(listInvestors);
      makeScroll(position);
    },
    onError: (error) => {
      console.error(error);
      if (listInvestorsData) {
        setListInvestor(listInvestorsData.listInvestors || { ...initialListValue });
      }
    }
  });

  const {
    data: listEntityData,
    refetch: listEntityRefetch,
    loading: listEntityLoading
  } = useQuery<FatInvestorsPageListEntitiesQuery>(LIST_ENTITY_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    skip: viewBy !== 'Investor Entity',
    variables: {
      data: {
        sortBy: key,
        sortDirection: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
        search: q.toLowerCase().trim(),
        limit: Number(limit),
        offset: Number(offset),
        includeRecommendations: toggle,
        filter: ['All Advisory Firms'].includes(primaryFilter) ? null : primaryFilter
      }
    },
    onCompleted: ({ listEntities }: { listEntities: FatInvestorsPageListEntitiesQuery['listEntities'] }) => {
      setListEntity(listEntities);
      makeScroll(position);
    },
    onError: (error) => {
      console.error(error);
      if (listEntityData) {
        setListEntity(listEntityData.listEntities || { ...initialListValue });
      }
    }
  });

  const getCurrentStatus = (): CurrentStatus => {
    switch (viewBy) {
      case 'Advisory Firm':
        return {
          loading: listAdvisorLoading,
          refetch: listAdvisorRefetch,
          total: listAdvisors.total
        };
      case 'Investor Household':
        return {
          loading: listInvestorsLoading,
          refetch: listInvestorsRefetch,
          total: listInvestor.total
        };
      case 'Investor Entity':
        return {
          loading: listEntityLoading,
          refetch: listEntityRefetch,
          total: listEntity.total
        };
      default:
        return {
          loading: false,
          refetch: () => null as any,
          total: 0
        };
    }
  };
  const openAdvisorFirmDetails = (id: string) => {
    onChangeViewBy('Investor Household');
    const selectedAdvisor = listAdvisors.data.find((item) => item.id === id)?.name || 'All Advisory Firms';
    onChangePrimaryFilter(selectedAdvisor);
  };

  const openInvestorDetailsPage = async ({ investorId, entityId, isGroup }: { investorId: string; entityId?: string; isGroup?: boolean }) => {
    try {
      if (entityId) {
        saveScrollPostion();
        navigate(`${investorId}/${entityId}?isGroup=${isGroup}&backToTitle=Investors`);
        return;
      }

      const { data } = await client.query({
        query: GET_INVESTOR_LEGAL_ENTITIES_IDS_QUERY,
        variables: {
          data: {
            id: investorId
          }
        }
      });

      const { legalEntities } = data.getInvestor;
      saveScrollPostion();

      if (legalEntities?.length === 1) {
        const entity = legalEntities?.[0];
        navigate(`${investorId}/${entity?.id}?isGroup=${entity?.isGroup}&backToTitle=Investors`);
        return;
      }

      navigate(`${investorId}?backToTitle=Investors`);
    } catch (error) {
      console.error(error);
    }
  };

  const openCreateInvestor = () => {
    // if (!user) return;
    // if (user.tenant.type === TenantType.PLATFORM_MANAGER) {
    //   setModalWindow({ isOpen: true, type: 'add-new-investor-entity' });
    //   return;
    // }
    saveScrollPostion();
    navigate(`/investors/create-investor?backToTitle=Investors`);
  };

  const onChangeViewBy = (viewByValue: string) => {
    setSearchParams(
      (prev) => {
        prev.set('viewBy', viewByValue);
        viewByValue === 'Advisory Firm' && prev.delete('primaryFilter');
        prev.delete('limit');
        prev.delete('offset');
        prev.delete('key');
        prev.delete('asc');
        return prev;
      },
      { replace: true }
    );
  };

  // const onViewByChange = (value: string) => {
  //   if (user) {
  //     onChangeViewBy(value);
  //     if (primaryFilter !== 'All Investors' && primaryFilter !== 'All Advisory Firms') return;
  //     const { tenant } = user;
  //     if (tenant.type === TenantType.ADVISORY_FIRM) {
  //       setPrimaryFilter('All Investors');
  //       return;
  //     }

  //     if (tenant.type === TenantType.PLATFORM_MANAGER) {
  //       setPrimaryFilter('All Advisory Firms');
  //     }
  //   }
  // };

  if (getTotalListAdvisorsLoading || getTotalListInvestorsLoading || getTotalListEntityLoading) {
    return <LoaderOnWholeScreen />;
  }

  if (firmsTotal !== null && firmsTotal === 0 && investorsTotal !== null && investorsTotal === 0 && entitiesTotal !== null && entitiesTotal === 0) {
    return (
      <>
        <MainWrap>
          <Header
            specialControl={<SpecialControlHeader switcher={toggle} setSwitcher={() => onChangeToggle(toggle)} title="Include Pending Commitments" />}
          />
        </MainWrap>
        <MainWrap>
          <PaddingWrap>
            <NoInvestorsWrapper>
              <NoInvestorsTitle>
                <img src="/static/investors-page/missing-mag-glass-graphic.png" draggable="false" alt="No Investors" />
                <div>
                  <h2>No Investors Added.</h2>
                  <p>You can start by adding an investor below.</p>
                </div>
              </NoInvestorsTitle>
              <Divider />
              <NoInvestorsSteps>
                <NoInvestorsStep>
                  <AddPageIcon />
                  <p>Add or bulk upload Investors</p>
                </NoInvestorsStep>
                <NoInvestorsStepSeparator />
                <NoInvestorsStep>
                  <GearPageIcon />
                  <p>Provide Investor Documentation</p>
                </NoInvestorsStep>
                <NoInvestorsStepSeparator />
                <NoInvestorsStep>
                  <DollarSignCircleIcon />
                  <p>Request Allocations for desired amount</p>
                </NoInvestorsStep>
                <NoInvestorsStepSeparator />
                <NoInvestorsStep>
                  <HistoryIcon />
                  <p>Check commitment status at any time</p>
                </NoInvestorsStep>
              </NoInvestorsSteps>
              <NoInvestorsButtons>
                <Button size="lg" onClick={() => openCreateInvestor()}>
                  Add Investor
                </Button>
                {/* <Button size="lg" onClick={() => navigate('/settings?action=importData&type=investorEntities')} styleType="outline">
                  Bulk Upload
                </Button> */}
              </NoInvestorsButtons>
            </NoInvestorsWrapper>
          </PaddingWrap>
        </MainWrap>
      </>
    );
  }

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'add-new-investor-entity' && (
        <AddNewInvestorEntity
          isOpen={modalWindow.isOpen}
          advisoryFirmList={advisoryFirmListData?.allAdvisoryFirmList || []}
          onClose={() => setModalWindow({ ...modalWindow, isOpen: false })}
        />
      )}
      <MainWrap>
        <Header />
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          <AddButtonWrap>
            <Button onClick={() => openCreateInvestor()} icon={<AddButtonIcon />} size="md">
              Add New Investor
            </Button>
          </AddButtonWrap>
          <FilterPage
            search={{
              value: q,
              onChange: onChangeSearch,
              placeholder: 'Search'
            }}
            resultsValue={getCurrentStatus().total}
            refetch={getCurrentStatus().refetch}
            viewByQuickFilter={{
              title: 'View By',
              value: viewBy,
              defaultValue: 'All',
              options: viewByList,
              onChange: onChangeViewBy
            }}
            {...(user &&
              user.tenant.type === TenantType.PLATFORM_MANAGER &&
              viewBy !== 'Advisory Firm' && {
                advisoryFirmQuickFilter: {
                  title: 'Advisory Firm',
                  value: primaryFilter,
                  defaultValue: 'All',
                  options: primaryFilterList,
                  onChange: onChangePrimaryFilter
                }
              })}
            toggle={{
              title: 'Include Pending Commitments',
              isActive: toggle,
              onToggle: () => onChangeToggle(toggle)
            }}
            mobileSort={{
              value: mobileSort,
              onChange: setMobileSort,
              selects: mobileSortList
            }}
            useNewFilters
          />
          <Table
            sort={{ key, asc }}
            setSort={onChangeSort}
            refetch={() => null}
            loading={getCurrentStatus().loading}
            viewByValue={viewBy}
            openAdvisorFirmDetails={openAdvisorFirmDetails}
            openInvestorDetailsPage={openInvestorDetailsPage}
            listAdvisers={listAdvisors}
            listInvestors={listInvestor}
            listEntity={listEntity}
          />
          <PaginationWrap>
            <TablePagination
              savePagination={onChangePaginationData}
              paginationValues={{
                limit: Number(limit),
                offset: Number(offset),
                total: getCurrentStatus().total
              }}
              refetch={() => null as any}
            />
          </PaginationWrap>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const PaddingWrap = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  @media (min-width: 600px) {
    padding-left: 50px;
    padding-right: 50px;
  }
`;

const AddButtonWrap = styled.div`
  margin-top: 20px;
`;

const NoInvestorsWrapper = styled.div`
  margin: 100px;
`;

const NoInvestorsTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  color: ${themeVar('font.weak')};
  padding-bottom: 40px;
`;

const NoInvestorsSteps = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const NoInvestorsStep = styled.div`
  display: flex;
  width: 200px;
  height: 139px;
  padding: 10px;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 15px;
  color: ${themeVar('font.base')};
  text-align: center;
`;

const NoInvestorsStepSeparatorWrapper = styled.div`
  display: flex;
  width: 40px;
  height: 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${themeVar('font.weak')};
  & > div {
    width: 20px;
    height: 12.35px;
    flex-shrink: 0;
  }
`;

const NoInvestorsStepSeparator = () => (
  <NoInvestorsStepSeparatorWrapper>
    <div>
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="21" viewBox="0 0 14 21" fill="none">
        <path d="M0.856689 2.85001L8.48669 10.5L0.856689 18.15L3.20669 20.5L13.2067 10.5L3.20669 0.5L0.856689 2.85001Z" fill="#ABABAB" />
      </svg>
    </div>
  </NoInvestorsStepSeparatorWrapper>
);

const NoInvestorsButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding-top: 40px;
`;

const Divider = styled.div`
  display: flex;
  background: #c7cbd2;
  height: 1px;
  width: 110%;
  margin-left: -20px;
  margin-right: -20px;
`;

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
